import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Package, ShoppingBag, Plus, Upload, Trash2, Edit, Eye, EyeOff, 
  Loader2, BookOpen, Euro, Download, TrendingUp, FileText
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminShopPage = () => {
  const { token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState({ total: 0, total_revenue: 0, sales: [] });
  const [activeTab, setActiveTab] = useState('products');
  
  // New product form
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    title: '',
    description: '',
    price: '',
    category: 'psychologie'
  });
  const [creating, setCreating] = useState(false);
  
  // Edit product
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isAdmin, navigate]);

  const fetchData = async () => {
    try {
      const [productsRes, salesRes] = await Promise.all([
        axios.get(`${API}/admin/shop/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/shop/sales`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setProducts(productsRes.data);
      setSales(salesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    
    if (!newProduct.title || !newProduct.price) {
      toast.error('Titre et prix requis');
      return;
    }
    
    setCreating(true);
    try {
      const params = new URLSearchParams({
        title: newProduct.title,
        price: newProduct.price,
        category: newProduct.category
      });
      if (newProduct.description) {
        params.append('description', newProduct.description);
      }
      
      const response = await axios.post(
        `${API}/admin/shop/products?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Produit créé ! Uploadez maintenant les fichiers.');
      setShowNewProduct(false);
      setNewProduct({ title: '', description: '', price: '', category: 'psychologie' });
      fetchData();
      
      // Set editing to upload files
      setEditingProduct({ id: response.data.id, ...newProduct });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleUploadFile = async (productId, file, type) => {
    setUploading(`${productId}-${type}`);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const endpoint = type === 'cover' 
        ? `${API}/admin/shop/products/${productId}/upload-cover`
        : `${API}/admin/shop/products/${productId}/upload-file`;
      
      await axios.post(endpoint, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(type === 'cover' ? 'Couverture uploadée' : 'Fichier uploadé');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(null);
    }
  };

  const handleToggleActive = async (productId, currentStatus) => {
    try {
      await axios.put(
        `${API}/admin/shop/products/${productId}`,
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(currentStatus ? 'Produit désactivé' : 'Produit activé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    
    try {
      await axios.delete(`${API}/admin/shop/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Produit supprimé');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-sage-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="container-app py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
              <ShoppingBag className="w-6 h-6 text-purple-600" />
              Gestion de la boutique
            </h1>
            <p className="text-muted-foreground">Gérez vos livres numériques et vos ventes</p>
          </div>
          
          <Dialog open={showNewProduct} onOpenChange={setShowNewProduct}>
            <DialogTrigger asChild>
              <Button className="bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau livre
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Ajouter un livre</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateProduct} className="space-y-4">
                <div>
                  <Label>Titre *</Label>
                  <Input 
                    value={newProduct.title}
                    onChange={(e) => setNewProduct({ ...newProduct, title: e.target.value })}
                    placeholder="Titre du livre"
                    required
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea 
                    value={newProduct.description}
                    onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                    placeholder="Description du livre..."
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Prix (€) *</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    min="0"
                    value={newProduct.price}
                    onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                    placeholder="9.99"
                    required
                  />
                </div>
                <div>
                  <Label>Catégorie</Label>
                  <Input 
                    value={newProduct.category}
                    onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                    placeholder="psychologie"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={creating}>
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Créer le produit
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-sand-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Produits</span>
              <Package className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-2xl font-semibold mt-1">{products.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-sand-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Ventes</span>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-2xl font-semibold mt-1">{sales.total}</p>
          </div>
          <div className="bg-white rounded-xl border border-sand-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Revenus</span>
              <Euro className="w-5 h-5 text-amber-500" />
            </div>
            <p className="text-2xl font-semibold mt-1">{sales.total_revenue.toFixed(2)}€</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="products">
              <BookOpen className="w-4 h-4 mr-2" />
              Produits ({products.length})
            </TabsTrigger>
            <TabsTrigger value="sales">
              <Euro className="w-4 h-4 mr-2" />
              Ventes ({sales.total})
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="bg-white rounded-xl border border-sand-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-sand-50 border-b border-sand-200">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium">Livre</th>
                    <th className="text-left p-4 text-sm font-medium">Prix</th>
                    <th className="text-left p-4 text-sm font-medium">Fichiers</th>
                    <th className="text-left p-4 text-sm font-medium">Statut</th>
                    <th className="text-right p-4 text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-sand-100">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-sand-50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {product.cover_image_url ? (
                            <img 
                              src={`${process.env.REACT_APP_BACKEND_URL}${product.cover_image_url}`}
                              alt=""
                              className="w-10 h-14 object-cover rounded"
                            />
                          ) : (
                            <div className="w-10 h-14 bg-purple-100 rounded flex items-center justify-center">
                              <BookOpen className="w-5 h-5 text-purple-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{product.title}</p>
                            <p className="text-xs text-muted-foreground capitalize">{product.category}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="font-semibold">{product.price}€</span>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col gap-1">
                          {/* EPUB Upload */}
                          <label className="cursor-pointer">
                            <input 
                              type="file"
                              accept=".epub,.pdf"
                              className="hidden"
                              onChange={(e) => e.target.files[0] && handleUploadFile(product.id, e.target.files[0], 'file')}
                            />
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                              product.file_url ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {uploading === `${product.id}-file` ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <FileText className="w-3 h-3" />
                              )}
                              {product.file_url ? 'EPUB ✓' : 'Ajouter EPUB'}
                            </span>
                          </label>
                          
                          {/* Cover Upload */}
                          <label className="cursor-pointer">
                            <input 
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => e.target.files[0] && handleUploadFile(product.id, e.target.files[0], 'cover')}
                            />
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                              product.cover_image_url ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {uploading === `${product.id}-cover` ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Upload className="w-3 h-3" />
                              )}
                              {product.cover_image_url ? 'Couverture ✓' : 'Ajouter couverture'}
                            </span>
                          </label>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                          product.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {product.is_active ? (
                            <><Eye className="w-3 h-3" /> Actif</>
                          ) : (
                            <><EyeOff className="w-3 h-3" /> Inactif</>
                          )}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(product.id, product.is_active)}
                          >
                            {product.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteProduct(product.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  
                  {products.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-muted-foreground">
                        Aucun produit. Cliquez sur "Nouveau livre" pour commencer.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Sales Tab */}
          <TabsContent value="sales">
            <div className="bg-white rounded-xl border border-sand-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-sand-50 border-b border-sand-200">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium">Date</th>
                    <th className="text-left p-4 text-sm font-medium">Produit</th>
                    <th className="text-left p-4 text-sm font-medium">Client</th>
                    <th className="text-left p-4 text-sm font-medium">Montant</th>
                    <th className="text-left p-4 text-sm font-medium">Statut</th>
                    <th className="text-left p-4 text-sm font-medium">Téléchargements</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-sand-100">
                  {sales.sales.map((sale) => (
                    <tr key={sale.id} className="hover:bg-sand-50">
                      <td className="p-4 text-sm">
                        {sale.purchased_at ? format(new Date(sale.purchased_at), 'dd/MM/yyyy HH:mm', { locale: fr }) : '-'}
                      </td>
                      <td className="p-4">
                        <p className="font-medium">{sale.product_title || 'Produit supprimé'}</p>
                      </td>
                      <td className="p-4 text-sm">
                        {sale.user_email}
                      </td>
                      <td className="p-4">
                        <span className="font-semibold">{sale.amount}€</span>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center text-xs px-2 py-1 rounded-full ${
                          sale.status === 'completed' ? 'bg-green-100 text-green-700' : 
                          sale.status === 'pending' ? 'bg-amber-100 text-amber-700' : 
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {sale.status === 'completed' ? 'Payé' : sale.status === 'pending' ? 'En attente' : sale.status}
                        </span>
                      </td>
                      <td className="p-4 text-sm">
                        <span className="inline-flex items-center gap-1">
                          <Download className="w-3 h-3" />
                          {sale.download_count}
                        </span>
                      </td>
                    </tr>
                  ))}
                  
                  {sales.sales.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-muted-foreground">
                        Aucune vente pour le moment.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminShopPage;
